import { styled } from "@mui/system";
import React from "react";
import TimePeriodDropdown from "src/components/portfolio/balance/TimePeriodDropdown";
import { ITimePeriodOption } from "src/components/portfolio/defaults";
import api from "src/store/features/api";
import { getShortMonthFromDate } from "src/utils/helpers/DateAndTimeHelpers";
import { v4 as uuidv4 } from "uuid";
import GraphBar from "./GraphBar";
import useDeviceSizes from "src/components/portfolio/hooks/useDeviceSizes";
import { LinearProgress } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useUserSlice } from "src/store/features/user.slice";
import { handleToFixedAmount } from "src/components/portfolio/helpers";
import { dynamicPriceFormatter } from "src/utils/helpers/PriceAndUnitsHelpers";

export const Wrapper = styled("div")(() => ({
  display: "flex",
  width: "100%",
  padding: "16px 0px",
  flexDirection: "column",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  gap: "32px",
  flex: "1 0 0",
}));

export const TitleAndFilter = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  alignSelf: "stretch",
}));

export const BarsWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "26px",
  flex: "1 0 0",
  alignSelf: "stretch",

  [theme.breakpoints.down(720)]: {
    justifyContent: "space-between",
  },
}));

export const EmptyBarsWrapper = styled("div")(() => ({
  display: "flex",
  height: "153px",
  justifyContent: "center",
  alignItems: "center",
  // gap: "26px",
  alignSelf: "stretch",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

export const UnverifiedLink = styled(RouterLink)({
  color: "#FC4C4C",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
  textDecorationLine: "underline",
  padding: "0px 4px",

  "&:hover": {
    color: "#FEDBDB",
  },
});

interface TextProps {
  color?: string;
  fontSize?: string;
  lineHeight?: string;
  bold?: boolean;
}
export const Text = styled("span")<TextProps>(({ color, fontSize, lineHeight, bold }) => ({
  color: color ?? "#002035",
  fontFamily: "Söhne",
  fontSize: fontSize ?? "14px",
  fontStyle: "normal",
  fontWeight: bold ? 600 : 400,
  lineHeight: lineHeight ?? "17px",
}));

export interface IBar {
  id: string;
  background: string;
  value: string;
  month: string;
  maxValue: number;
}

const MONTHS_TEMPLATE = [
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Jan",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Feb",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Mar",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Apr",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "May",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Jun",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Jul",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Aug",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Sep",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Oct",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Nov",
    maxValue: 0,
  },
  {
    id: uuidv4(),
    background: "transparent",
    value: "",
    month: "Dec",
    maxValue: 0,
  },
];

const TIME_PERIOD_OPTIONS: ITimePeriodOption[] = [
  { name: "3 Months", value: "3M", label: "3 Months" },
  { name: "6 Months", value: "6M", label: "6 Months" },
  { name: "9 Months", value: "9M", label: "9 Months" },
  { name: "1 Year", value: "1Y", label: "1 Year" },
];

const TIME_PERIOD_OPTIONS_MOBILE_RAF: ITimePeriodOption[] = [
  { name: "3 Months", value: "3M", label: "3 Months" },
  { name: "6 Months", value: "6M", label: "6 Months" },
];

// Function to replace elements in the template array
const replaceElementsInTemplate = (template: IBar[], replacements: IBar[]) => {
  return template.map((monthTemplate) => {
    const replacement = replacements.find(
      (replacement) => replacement.month === monthTemplate.month,
    );
    if (replacement) {
      return { ...monthTemplate, ...replacement };
    } else {
      return monthTemplate;
    }
  });
};

// Function to get the index of the current month
const getCurrentMonthIndex = () => {
  const currentMonth = new Date().getMonth() + 1;
  return currentMonth - 1; // Adjust for zero-based index
};

// Function to reorder the months array
const reorderMonths = (template: IBar[]) => {
  const currentMonthIndex = getCurrentMonthIndex();
  const beforeCurrentMonth = template.slice(0, currentMonthIndex);
  const afterCurrentMonth = template.slice(currentMonthIndex);
  return [...afterCurrentMonth, ...beforeCurrentMonth];
};

const EarningsGraph = () => {
  const { isMobile } = useDeviceSizes();

  const { isUnverified } = useUserSlice();

  const [timePeriod, setTimePeriod] = React.useState<string>(
    isMobile ? TIME_PERIOD_OPTIONS_MOBILE_RAF[1].name : TIME_PERIOD_OPTIONS[3].name,
  );
  const [timePeriodValue, setTimePeriodValue] = React.useState<ITimePeriodOption["value"]>(
    isMobile ? TIME_PERIOD_OPTIONS_MOBILE_RAF[1].value : TIME_PERIOD_OPTIONS[3].value,
  );
  const handleTimePeriod = (newOpt: ITimePeriodOption): void => {
    setTimePeriod(newOpt.label);
    setTimePeriodValue(newOpt.value);
  };

  React.useEffect(() => {
    if (isMobile) {
      setTimePeriod(TIME_PERIOD_OPTIONS_MOBILE_RAF[1].label);
      setTimePeriodValue(TIME_PERIOD_OPTIONS_MOBILE_RAF[1].value);
    } else {
      setTimePeriod(TIME_PERIOD_OPTIONS[3].label);
      setTimePeriodValue(TIME_PERIOD_OPTIONS[3].value);
    }
  }, [isMobile]);

  // get user RaF data
  const {
    data: rewardsMonthly,
    isLoading,
    isFetching,
  } = api.useGetReferralMontlyRewardsQuery({
    timeFrame: timePeriodValue,
  });

  const generatedBars = React.useMemo((): IBar[] => {
    if (!rewardsMonthly || rewardsMonthly.length <= 0) return [];

    // Generate bars based on rewards monthly data
    const bars = rewardsMonthly.map((reward): IBar => {
      const currentMonth = new Date().getMonth() + 1;
      const month = new Date(reward.timestamp).getMonth() + 1;
      const background =
        reward.value <= 0 ? "transparent" : currentMonth === month ? "#FC4C4C" : "#002035";

      return {
        id: uuidv4(),
        background,
        value: `${handleToFixedAmount(reward.value)}`,
        month: getShortMonthFromDate(reward.timestamp ?? ""),
        maxValue: Math.max(...rewardsMonthly.map((reward) => reward.value)),
      };
    });

    // Sort and replace template with actual data
    const sortedTemplate = reorderMonths(MONTHS_TEMPLATE);
    const replaced = replaceElementsInTemplate(sortedTemplate, bars).reverse();

    // Reverse all elements besides the last
    const reversedExceptLast = replaced
      .slice(0, -1) // Exclude the last element
      .reverse(); // Reverse the resulting array

    // Concatenate the last element to the reversed array
    const reversedArray = [...reversedExceptLast, replaced[replaced.length - 1]];

    if (isMobile) {
      if (timePeriodValue === "3M") return reversedArray.slice(9);
      if (timePeriodValue === "6M") return reversedArray.slice(6);
    }

    return reversedArray;
  }, [rewardsMonthly, isMobile]);

  const totalEarnings = React.useMemo(() => {
    if (!rewardsMonthly) return 0;

    return handleToFixedAmount(rewardsMonthly.reduce((acc, { value }) => acc + value, 0));
  }, [rewardsMonthly]);

  return (
    <Wrapper>
      <TitleAndFilter>
        <Text>Your earnings</Text>
        <TimePeriodDropdown
          options={isMobile ? TIME_PERIOD_OPTIONS_MOBILE_RAF : TIME_PERIOD_OPTIONS}
          timePeriod={timePeriod}
          onTimePeriodChange={handleTimePeriod}
        />
      </TitleAndFilter>
      {isFetching || isLoading ? (
        // If fetching, show linear progress
        <div style={{ width: "100%" }}>
          <LinearProgress color="primary" />
        </div>
      ) : generatedBars.length <= 0 ? (
        // If no data, show message
        <>
          {isUnverified ? (
            <EmptyBarsWrapper>
              You have no earnings yet.. Verify your account
              <UnverifiedLink to="/application"> here </UnverifiedLink>
              to begin
            </EmptyBarsWrapper>
          ) : (
            <EmptyBarsWrapper>You have no earnings so far...</EmptyBarsWrapper>
          )}
        </>
      ) : (
        // If data is available, render graph bars
        <>
          <BarsWrapper>
            {generatedBars.map((bar) => (
              <GraphBar key={bar.id} bar={bar} />
            ))}
          </BarsWrapper>
          <Text>
            You have earned <Text bold>{dynamicPriceFormatter(Number(totalEarnings))} USDC</Text> so
            far.
          </Text>
        </>
      )}
    </Wrapper>
  );
};

export default EarningsGraph;
